import { Box, CircularProgress, Alert } from '@mui/material';
import { ArtistExplorer } from 'components/collab-club/artist-explorer';
import { ConnectConfirmationModal } from 'components/collab-club/connect-confirmation-modal';
import { SuccessModal } from 'components/collab-club/success-modal';
import { ProfileSelector } from 'components/collab-club/profile-selector';
import { OnboardingModal } from 'components/collab-club/onboarding-modal';
import React, { useState, useEffect, useCallback } from 'react';
import { useArtistLikes } from 'hooks/swrRequests/useArtistLikes';
import { useSubmitCollabRequest } from 'hooks/swrRequests/useCollabRequests';
import { useCollabStats } from 'hooks/swrRequests/useCollabStats';
import { useMyCcArtists } from 'hooks/swrRequests/useMyCcArtists';
import { Artist } from 'types';

const CollabClub: React.FC = () => {
  const [selectedArtists, setSelectedArtists] = useState<Map<string, Artist>>(new Map());
  const { likedArtistIds, toggleLike } = useArtistLikes();
  const { submit, error: submitError } = useSubmitCollabRequest();
  const [selectedProfile, setSelectedProfile] = useState<Artist | null>(null);
  const {
    stats: connectionStats,
    remainingConnections,
    refetch: refetchStats,
  } = useCollabStats(selectedProfile?.id);
  const { myArtists, isLoading: isLoadingProfiles, refetch: refetchProfiles } = useMyCcArtists();
  const [showOnlyLiked, setShowOnlyLiked] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedArtistCount, setSubmittedArtistCount] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Auto-select first profile when profiles are loaded
  useEffect(() => {
    if (myArtists.length > 0 && !selectedProfile) {
      setSelectedProfile(myArtists[0]);
    }
  }, [myArtists, selectedProfile]);

  // Check for onboarding need when selected profile changes
  useEffect(() => {
    if (selectedProfile && !selectedProfile.isOnboarded) {
      setShowOnboarding(true);
    } else {
      setShowOnboarding(false);
    }
  }, [selectedProfile]);

  const toggleArtistSelection = (artistId: string, artist: Artist) => {
    const newSelected = new Map(selectedArtists);
    if (newSelected.has(artistId)) {
      newSelected.delete(artistId);
    } else {
      // Only allow selection if we have connection stats and haven't reached the limit
      if (connectionStats && newSelected.size < connectionStats.remainingConnections) {
        newSelected.set(artistId, artist);
      }
    }
    setSelectedArtists(newSelected);
  };

  const handleConnectSelected = () => {
    setShowConnectModal(true);
  };

  const handleConfirmConnect = async () => {
    if (selectedArtists.size === 0 || !selectedProfile) return;

    const artistIds = Array.from(selectedArtists.keys()).map(Number);
    const result = await submit({ ccArtistIds: artistIds, fromCcArtistId: selectedProfile.id });

    if (result.success) {
      setSubmittedArtistCount(selectedArtists.size);
      setShowConnectModal(false);
      setShowSuccessModal(true);
      setSelectedArtists(new Map());
      // Refresh stats after successful submission
      refetchStats();
    }
    // If failed, keep modal open to show error
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    refetchProfiles(); // Refresh artist profiles to get updated data
  };

  // Get selected artist objects from the Map
  const getSelectedArtistObjects = useCallback(() => {
    return Array.from(selectedArtists.values());
  }, [selectedArtists]);

  if (isLoadingProfiles) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Profile Selector - only show if user has more than 1 profile */}
      {myArtists.length > 1 && (
        <Box sx={{ p: 2, borderBottom: '1px solid #e5e5e5' }}>
          <ProfileSelector
            profiles={myArtists}
            selectedProfile={selectedProfile}
            onProfileSelect={setSelectedProfile}
          />
        </Box>
      )}

      <Box sx={{ flex: 1 }}>
        {/* {myArtists.length > 0 ? ( */}
        <ArtistExplorer
          selectedArtists={selectedArtists}
          onToggleSelection={toggleArtistSelection}
          likedArtists={likedArtistIds}
          onToggleLike={(artistId) => toggleLike(parseInt(artistId), likedArtistIds.has(artistId))}
          showOnlyLiked={showOnlyLiked}
          onToggleShowOnlyLiked={setShowOnlyLiked}
          onConnectSelected={handleConnectSelected}
          connectionStats={connectionStats}
        />
        {/* ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              fontSize: '18px',
              px: { xs: 3 },
              py: { xs: 2 },
              textAlign: 'center',
              boxSizing: 'border-box',
            }}
          >
            Oops! This section is only available for users with an artist profile.
          </Box>
        )} */}
      </Box>

      <ConnectConfirmationModal
        isOpen={showConnectModal}
        onClose={() => setShowConnectModal(false)}
        selectedArtists={getSelectedArtistObjects()}
        onConfirm={handleConfirmConnect}
        error={submitError}
      />

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        artistCount={submittedArtistCount}
      />

      {/* Onboarding Modal */}
      {selectedProfile && (
        <OnboardingModal
          profile={selectedProfile}
          isOpen={showOnboarding}
          onComplete={handleOnboardingComplete}
        />
      )}
    </Box>
  );
};

export default CollabClub;
